
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ProductCTAProps {
  variant?: "inline" | "floating" | "banner";
  productName?: string;
  productImage?: string;
  productLink?: string;
  message?: string;
  linkType?: "itjustworks" | "breathe1" | "breathe2" | "skillano" | "heyhumans";
}

const ProductCTA = ({
  variant = "inline",
  productName = "It Just Works Deodorant",
  productImage = "/lovable-uploads/a38e4978-a37a-452c-a67e-140d9acc3d2e.png",
  productLink,
  message = "Try our recommended natural deodorant",
  linkType = "itjustworks"
}: ProductCTAProps) => {
  // Function to get the appropriate affiliate link based on linkType
  const getAffiliateLink = () => {
    // Use a sanitized approach to affiliate links
    switch (linkType) {
      case "breathe1":
        return "https://www.breathenaturals.com/r?id=slae8t";
      case "breathe2":
        return "https://www.breathenaturals.com/r?id=7ncs5u";
      case "skillano":
        return "https://www.skillano-beauty.com/?ref=sfvnconi";
      case "heyhumans":
        return "https://www.heyhumansusa.com/?dt_id=2391110";
      case "itjustworks":
      default:
        return "https://www.itjustworksdeodorant.com?sca_ref=8468108.vEEWUNzhD1";
    }
  };

  // Use the affiliate link based on linkType or the provided productLink
  const finalProductLink = productLink || getAffiliateLink();

  // State to track visibility
  const [isVisible, setIsVisible] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);

  // Handle scroll events for the floating variant
  useEffect(() => {
    if (variant !== "floating") return;
    
    const handleScroll = () => {
      // Get footer element
      const footer = document.querySelector('footer');
      
      if (!footer) return;
      
      // Get the position of the footer relative to the viewport
      const footerRect = footer.getBoundingClientRect();
      // Get viewport height
      const viewportHeight = window.innerHeight;
      
      // Show CTA when footer is about to enter viewport (100px before)
      const isFooterVisible = footerRect.top - viewportHeight <= 100;
      
      setIsVisible(isFooterVisible);
    };

    window.addEventListener("scroll", handleScroll);
    // Trigger once on mount to check initial position
    handleScroll();
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, [variant]);

  if (variant === "floating") {
    return (
      <div 
        className={`fixed bottom-6 left-6 z-50 max-w-xs transition-all duration-500 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"
        }`}
      >
        <div className="flex items-center gap-4 backdrop-blur-xl bg-white/20 dark:bg-black/20 shadow-lg rounded-lg p-4 border border-white/20 dark:border-white/10 animate-in slide-in-from-bottom-10 duration-300">
          <img src={productImage} alt={productName} className="w-16 h-16 object-cover rounded" loading="lazy" />
          <div>
            <p className="text-sm font-medium mb-1 text-foreground">{message}</p>
            <Button asChild size="sm" className="w-full">
              <a 
                href={finalProductLink} 
                target="_blank" 
                rel="noopener noreferrer nofollow" 
                className="inline-flex items-center"
                ref={linkRef}
                aria-label={`Shop ${productName}`}
              >
                <ShoppingBag className="mr-1 h-3 w-3" />
                Shop Now
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "banner") {
    return (
      <div className="bg-primary/5 backdrop-blur-sm py-4 px-6 rounded-lg shadow-sm border border-primary/10 mb-6">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <img src={productImage} alt={productName} className="w-16 h-16 object-cover rounded" loading="lazy" />
          <div className="flex-grow text-center sm:text-left">
            <p className="font-medium mb-1">{message}</p>
            <p className="text-sm text-muted-foreground mb-2">High-quality natural deodorant for healthier armpits</p>
          </div>
          <Button asChild>
            <a 
              href={finalProductLink} 
              target="_blank" 
              rel="noopener noreferrer nofollow" 
              className="whitespace-nowrap"
              aria-label={`Shop ${productName}`}
            >
              <ShoppingBag className="mr-2 h-4 w-4" />
              Shop Now
            </a>
          </Button>
        </div>
      </div>
    );
  }

  // Default inline variant
  return (
    <div className="flex items-center gap-4 bg-primary/5 rounded-lg p-4 my-4 border border-primary/10">
      <img src={productImage} alt={productName} className="w-14 h-14 object-cover rounded" loading="lazy" />
      <div className="flex-grow">
        <p className="text-sm font-medium">{message}</p>
      </div>
      <Button asChild size="sm">
        <a 
          href={finalProductLink} 
          target="_blank" 
          rel="noopener noreferrer nofollow"
          aria-label={`Shop ${productName}`}
        >
          Shop
        </a>
      </Button>
    </div>
  );
};

export default ProductCTA;
